import React, { useEffect, useState } from "react";
import { gameActor } from "../hooks/useGameMachine.js";
import { listSlots, load, type SaveSlotSummary } from "../../engine/persistence/PersistenceManager.js";
import { bootAdventure } from "../../engine/adventure/bootAdventure.js";
import {
  tryDeepLinkBoot,
  type DeepLinkOutcome,
} from "../../engine/adventure/bootFromUrl.js";

/**
 * Root launch screen (P21).
 *
 * Three options:
 * - **Continue** — resume the slot with the most recent timestamp across every
 *   adventure. The adventure is derived from the slot's `adventureId` — no
 *   "last session" pointer is stored anywhere.
 * - **New Game** — transition to the adventure picker.
 * - **Load Game** — transition to the save picker.
 *
 * Continue is the only button that does any async work up front; it scans
 * `listSlots()` on mount so the button can be disabled (and greyed) when no
 * save exists.
 */
export function LaunchScreen() {
  const [mostRecent, setMostRecent] = useState<SaveSlotSummary | null>(null);
  const [scanning, setScanning] = useState(true);
  const [continueBusy, setContinueBusy] = useState(false);
  const [continueError, setContinueError] = useState<string | null>(null);
  const [deepLink, setDeepLink] = useState<DeepLinkOutcome | null>(null);

  // AS30: if the URL carries ?adventure=X&map=Y&cell=x,z, boot straight
  // into that map using the most recent save for the adventure. Dev-only
  // so production bundles don't waste a listSlots() round-trip.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    let cancelled = false;
    void tryDeepLinkBoot().then((outcome) => {
      if (!cancelled) setDeepLink(outcome);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    listSlots()
      .then((slots) => {
        if (cancelled) return;
        const sorted = [...slots].sort((a, b) => b.meta.timestamp.localeCompare(a.meta.timestamp));
        setMostRecent(sorted[0] ?? null);
      })
      .catch((e: unknown) => {
        if (!cancelled) setMostRecent(null);
        console.warn("[launch] listSlots failed:", e);
      })
      .finally(() => {
        if (!cancelled) setScanning(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onContinue = async () => {
    if (!mostRecent) return;
    setContinueBusy(true);
    setContinueError(null);
    try {
      await bootAdventure(mostRecent.adventureId);
      const result = await load(mostRecent.slotId);
      // stores are now populated; kick the machine into `loading` via LOAD_MAP
      // so main.tsx's subscribe picks it up and builds the scene.
      gameActor.send({
        type: "LOAD_MAP",
        mapId: result.file.state.pointer.mapId,
        targetCell: result.file.state.pointer.position,
      });
    } catch (e: unknown) {
      setContinueError(e instanceof Error ? e.message : String(e));
      setContinueBusy(false);
    }
  };

  const continueDisabled = scanning || !mostRecent || continueBusy;

  return (
    <div data-testid="launch-screen" style={rootStyle}>
      <div style={panelStyle}>
        <div style={titleStyle}>bkcrawl</div>
        <div style={tagStyle}>a first-person grid crawler</div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 32 }}>
          <button
            type="button"
            data-testid="launch-continue-btn"
            disabled={continueDisabled}
            onClick={() => void onContinue()}
            style={{
              ...btnStyle,
              background: continueDisabled ? "#222" : "#2a6",
              color: continueDisabled ? "#666" : "white",
              cursor: continueDisabled ? "default" : "pointer",
            }}
          >
            {continueBusy ? "Loading…" : scanning ? "Continue" : mostRecent ? `Continue — ${mostRecent.displayName}` : "Continue (no saves)"}
          </button>

          <button
            type="button"
            data-testid="launch-new-btn"
            onClick={() => {
              gameActor.send({ type: "SHOW_ADVENTURE_PICKER" });
            }}
            style={btnStyle}
          >
            New Game
          </button>

          <button
            type="button"
            data-testid="launch-load-btn"
            onClick={() => {
              gameActor.send({ type: "SHOW_SAVE_PICKER" });
            }}
            style={btnStyle}
          >
            Load Game
          </button>
        </div>

        {continueError && (
          <div style={errorStyle}>Failed to continue: {continueError}</div>
        )}
        {deepLink?.kind === "no-save" && (
          <div style={deepLinkNoteStyle}>
            Deep link requested <code>{deepLink.adventureId}</code> · map{" "}
            <code>{deepLink.mapId}</code>, but no save exists for that adventure.
            Start a New Game to create a party first.
          </div>
        )}
        {deepLink?.kind === "error" && (
          <div style={errorStyle}>Deep link failed: {deepLink.message}</div>
        )}
      </div>
    </div>
  );
}

const rootStyle: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  background: "#050505",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "white",
  fontFamily: "monospace",
  pointerEvents: "auto",
  zIndex: 200,
};

const panelStyle: React.CSSProperties = {
  width: 380,
  padding: 32,
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
};

const titleStyle: React.CSSProperties = {
  fontSize: 36,
  letterSpacing: 8,
  textAlign: "center",
  color: "#ffd700",
  textShadow: "0 0 20px rgba(255,215,0,0.5)",
};

const tagStyle: React.CSSProperties = {
  fontSize: 11,
  color: "#888",
  textAlign: "center",
  marginTop: 4,
  letterSpacing: 2,
};

const btnStyle: React.CSSProperties = {
  padding: "12px 16px",
  background: "#1a1a1a",
  color: "white",
  border: "1px solid rgba(255,255,255,0.25)",
  borderRadius: 6,
  fontFamily: "monospace",
  fontSize: 14,
  cursor: "pointer",
  textAlign: "center",
};

const errorStyle: React.CSSProperties = {
  marginTop: 16,
  color: "#c66",
  fontSize: 12,
  textAlign: "center",
};

const deepLinkNoteStyle: React.CSSProperties = {
  marginTop: 16,
  color: "#ffd89c",
  fontSize: 11,
  textAlign: "center",
  lineHeight: 1.5,
};
